import { useEffect, useRef } from 'react';
import { DealStatusConfig, DealStatus, DEAL, MANDATE } from '../store/investor/reducer';
import { StatusDropdownOption } from '../common/_custom/StatusDropdown/StatusDropdown';
import { COLORS } from './constants/colors';
import { DEAL_VIEW_TYPES } from '../store/investor/constants';

type PollingOptions<T> = {
    fetchData: () => Promise<T>;
    onSuccess: (data: T) => void;
    shouldContinue?: (data: T) => boolean;
    interval?: number;
};

export function usePolling<T>({ fetchData, onSuccess, shouldContinue = () => true, interval = 5000 }: PollingOptions<T>) {
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stopPolling = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    useEffect(() => {
        let cancelled = false;

        const poll = async () => {
            try {
                const data = await fetchData();
                if (!cancelled) {
                    onSuccess(data);
                    if (!shouldContinue(data)) stopPolling();
                }
            } catch (err) {
                console.error('Polling error:', err);
                stopPolling();
            }
        };

        poll();
        intervalRef.current = setInterval(poll, interval);

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [fetchData, onSuccess, shouldContinue, interval]);
}

export const formatFollowUpDateTime = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${formattedHours}:${formattedMinutes} ${ampm}, ${day} ${month} ${year} (0 days ago)`;
};

type FallbackDealStatusOption = {
    [key: string]: StatusDropdownOption;
};

/**
 * Common utility function to get status dropdown option
 * Used in ConfigTableModal and DealsRow components
 */
export const getStatusDropdownOption = (
    status: string,
    dealStatusConfig: DealStatusConfig | null | undefined,
    selectedCategory: string,
    source?: string,
    fallbackDealStatusOption?: FallbackDealStatusOption,
): StatusDropdownOption => {
    // Default fallback option
    const defaultFallback: StatusDropdownOption = {
        label: 'Deal Shared',
        value: DealStatus.SHARED,
        backgroundColor: COLORS.PURPLE_TINT_50,
        labelColor: 'var(--primary-text-colour)',
        customCaret: {
            height: '14',
            width: '14',
            colour: 'var(--primary-text-colour)',
        },
    };

    // Check if dealStatusConfig is null or undefined
    if (!dealStatusConfig) {
        return fallbackDealStatusOption?.[DealStatus.SHARED] || defaultFallback;
    }

    const isInvestorDeal = source === 'investor-deal' || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
    let defaultStatus = isInvestorDeal ? DealStatus.SHARED : DealStatus.FIRST_DEAL_SHARED;
    if (status === DealStatus.DRAFT) defaultStatus = DealStatus.DRAFT;

    const options = isInvestorDeal ? dealStatusConfig[DEAL] || [] : dealStatusConfig[MANDATE] || [];
    const matchingOption = options.find((option) => option.value === status);
    const defaultMatchingOption = options.find((option) => option.value === defaultStatus);

    return (
        matchingOption ||
        defaultMatchingOption ||
        fallbackDealStatusOption?.[defaultStatus] ||
        fallbackDealStatusOption?.[DealStatus.SHARED] ||
        defaultFallback
    );
};

export const getKeysForVisiblityConfig = (defaultKey: string = '', entity: string, actionType: string, dropdownOption?: string) => {
    // Build base key - if defaultKey is empty, don't include the leading dot
    const baseKey = defaultKey ? `${defaultKey}.${entity}.${actionType}` : `${entity}.${actionType}`;
    // Return null for specificKey when no dropdownOption (simpler than returning baseKey)
    const specificKey = dropdownOption ? `${baseKey}.${dropdownOption}` : null;
    return {
        baseKey,
        specificKey,
    };
};

/**
 * Helper function to prepare file data with optional timestamp
 * Used to avoid duplicate file errors by appending timestamp to filename
 * @param fileData - The file data object containing file information
 * @param appendTimestamp - Whether to append timestamp to the filename
 * @param addTimeStampFn - Function to add timestamp to filename
 * @returns Object with originalFileName, processedFileName, and processedFileData
 */
export const prepareFileDataWithTimestamp = (fileData: any, appendTimestamp: boolean, addTimeStampFn: (fileName: string) => string) => {
    const originalFileName = fileData.file.name;
    const processedFileName = appendTimestamp ? addTimeStampFn(originalFileName) : originalFileName;

    // File object properties (name, size, type) are getters, so we explicitly include them
    // Also preserve custom properties like checkForDuplication
    const processedFileData = appendTimestamp
        ? {
              ...fileData,
              file: {
                  name: processedFileName,
                  size: fileData.file.size,
                  type: fileData.file.type,
                  ...(fileData.file.checkForDuplication && { checkForDuplication: fileData.file.checkForDuplication }),
              },
          }
        : fileData;

    return { originalFileName, processedFileName, processedFileData };
};
